import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import ListItemText from '@material-ui/core/ListItemText'
import ListSubheader from '@material-ui/core/ListSubheader'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Loader from 'components/general/Loader'
import PeriodSelect from 'components/general/PeriodSelect'
import AdminAPI from 'config/api/AdminAPI'
import BrokerAPI from 'config/api/BrokerAPI'
import { onError } from 'config/lib/errorLib'
import { getMoments } from 'config/helpers/Helpers'
import { productOptions } from 'config/constants/Constants'
import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import { NotificationManager } from 'react-notifications'
import moment from 'moment-timezone'

const useStyles = makeStyles(componentStyles)

const IP = ['income-protection']

const columnOptions = [
    { value: 'firstName', label: 'First Name', group: 'general' },
    { value: 'lastName', label: 'Last Name', group: 'general' },
    { value: 'email', label: 'Email', group: 'general' },
    { value: 'phone', label: 'Phone', group: 'general' },
    { value: 'dateAllocated', label: 'Date Allocated', group: 'general' },
    { value: 'product', label: 'Product', group: 'general' },
    { value: 'county', label: 'County', group: 'general' },
    { value: 'source', label: 'Source', group: 'general' },
    { value: 'stage', label: 'Pipeline Stage', group: 'general', adminOnly: true },
    { value: 'broker', label: 'Broker', group: 'general', adminOnly: true },
    { value: 'notes', label: 'Notes', group: 'general' },
    { value: 'dob', label: 'Date of Birth', group: 'personal', products: IP },
    { value: 'gender', label: 'Gender', group: 'personal', products: IP },
    { value: 'smokingStatus', label: 'Smoking Status', group: 'personal', products: IP },
    { value: 'maritalStatus', label: 'Marital Status', group: 'personal', products: IP },
    { value: 'address', label: 'Address', group: 'personal', products: IP },
    { value: 'city', label: 'Town/City', group: 'personal', products: IP },
    { value: 'eircode', label: 'Eircode', group: 'personal', products: IP },
    { value: 'country', label: 'Country', group: 'personal', products: IP },
    { value: 'employmentStatus', label: 'Employment Status', group: 'personal', products: IP },
    { value: 'occupation', label: 'Occupation', group: 'personal', products: IP },
    { value: 'salary', label: 'Salary', group: 'personal', products: IP },
    { value: 'majorHealthIssue', label: 'Major Health Issue', group: 'personal', products: IP },
    { value: 'quote.provider', label: 'Provider', group: 'quote', products: IP },
    { value: 'quote.premium', label: 'Monthly Premium (€)', group: 'quote', products: IP },
    { value: 'quote.coverAmount', label: 'Cover Amount (€)', group: 'quote', products: IP },
    { value: 'quote.policyTerm', label: 'Policy Term (years)', group: 'quote', products: IP },
    { value: 'quote.riskLevel', label: 'Risk Level', group: 'quote', products: IP },
    { value: 'employerSickPay', label: 'Employer Sick Pay', group: 'quote', products: IP }
]

const columnGroups = [
    { value: 'general', label: 'General' },
    { value: 'personal', label: 'Personal Details' },
    { value: 'quote', label: 'Quote Details' }
]

// Pseudo-option that toggles every quote column; never stored in the selection.
const ALL_QUOTE_FIELDS = '__allQuote'

const defaultColumns = ['firstName', 'lastName', 'email', 'phone', 'dateAllocated', 'stage']

function getOption(column) {
    return columnOptions.find((o) => o.value === column)
}

function isColumnAvailable(column, usesBrokerDiary, product) {
    const option = getOption(column)
    return !!option
        && (usesBrokerDiary || !option.adminOnly)
        && (!option.products || option.products.indexOf(product) > -1)
}

function ExportLeads() {
    const classes = useStyles()
    const theme = useTheme()
    const api = new AdminAPI()
    const brokerApi = new BrokerAPI()

    const [companies, setCompanies] = useState([])
    const [companyId, setCompanyId] = useState('')
    const [product, setProduct] = useState('pensions')
    const [brokers, setBrokers] = useState([])
    const [brokerId, setBrokerId] = useState('all')
    const [period, setPeriod] = useState('thisMonth')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [columns, setColumns] = useState(defaultColumns)
    const [loading, setLoading] = useState(true)
    const [exporting, setExporting] = useState(false)

    const selectedCompany = companies.find((c) => c.companyId === companyId)
    const usesBrokerDiary = !!(selectedCompany && selectedCompany.brokerDiary)
    const availableColumns = columnOptions.filter((o) => isColumnAvailable(o.value, usesBrokerDiary, product))
    const quoteColumns = availableColumns.filter((o) => o.group === 'quote').map((o) => o.value)
    const selectedQuoteCount = quoteColumns.filter((c) => columns.indexOf(c) > -1).length
    const allQuoteSelected = quoteColumns.length > 0 && selectedQuoteCount === quoteColumns.length

    useEffect(() => {
        getCompanies()
    }, [])

    async function getCompanies() {
        try {
            const dates = getMoments(period)
            let companies = await api.getCompanies()
            companies = companies.filter((c) => c.active)
            companies.sort((a, b) => a.name.localeCompare(b.name))

            setCompanies(companies)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    async function updateCompany(newCompanyId) {
        setCompanyId(newCompanyId)
        setBrokerId('all')
        setBrokers([])

        const company = companies.find((c) => c.companyId === newCompanyId)
        if (company && company.brokerDiary) {
            try {
                const brokers = await brokerApi.getBrokers(newCompanyId)
                const list = Array.isArray(brokers) ? brokers : []
                list.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
                setBrokers(list)
            } catch (e) {
                onError(e)
            }
        } else {
            // Non-BD companies have no pipeline/broker data — drop admin-only columns.
            setColumns((cols) => cols.filter((c) => isColumnAvailable(c, false, product)))
        }
    }

    function updateProduct(newProduct) {
        setProduct(newProduct)
        setColumns((cols) => cols.filter((c) => isColumnAvailable(c, usesBrokerDiary, newProduct)))
    }

    function handlePeriod(newPeriod, customFromDate = null, customToDate = null) {
        setPeriod(newPeriod)
        const dates = newPeriod === 'custom'
            ? { fromDate: moment(customFromDate).startOf('day'), toDate: moment(customToDate).endOf('day') }
            : getMoments(newPeriod)
        setFromDate(dates.fromDate)
        setToDate(dates.toDate)
    }

    function updateColumns(selected) {
        let next = selected.filter((v) => v !== ALL_QUOTE_FIELDS)
        if (selected.indexOf(ALL_QUOTE_FIELDS) > -1) {
            next = allQuoteSelected
                ? next.filter((v) => quoteColumns.indexOf(v) < 0)
                : next.concat(quoteColumns)
        }
        // Keep the canonical column order regardless of selection order.
        setColumns(columnOptions.map((o) => o.value).filter((v) => next.indexOf(v) > -1))
    }

    function downloadCsv(csv, fileName) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    async function handleExport() {
        if (!companyId) {
            NotificationManager.error('Please select a company.')
            return
        }
        if (!product) {
            NotificationManager.error('Please select a product.')
            return
        }
        if (columns.length === 0) {
            NotificationManager.error('Please select at least one column.')
            return
        }

        setExporting(true)
        try {
            const selectedBrokerId = usesBrokerDiary && brokerId !== 'all' ? brokerId : null
            const result = await api.exportLeads(
                companyId,
                product,
                selectedBrokerId,
                fromDate.valueOf(),
                toDate.valueOf(),
                columns
            )

            if (result && result.error) {
                NotificationManager.error(result.error)
                return
            }

            if (!result || !result.rowCount) {
                NotificationManager.warning('No leads found for the selected filters.')
                return
            }

            downloadCsv(result.csv, result.fileName)

            if (result.truncated) {
                NotificationManager.warning(
                    `Export capped at ${result.rowCount.toLocaleString()} of ${result.totalMatched.toLocaleString()} leads (file size limit). Narrow the period or select fewer columns to export the rest.`,
                    'Export truncated',
                    8000
                )
            } else {
                NotificationManager.success(`Exported ${result.rowCount.toLocaleString()} leads.`)
            }
        } catch (e) {
            onError(e)
        } finally {
            setExporting(false)
        }
    }

    const visibleGroups = columnGroups
        .map((g) => ({ ...g, options: availableColumns.filter((o) => o.group === g.value) }))
        .filter((g) => g.options.length > 0)

    // MUI v4 Select rejects Fragment children, so the grouped menu is built as a flat array.
    const columnMenuItems = []
    visibleGroups.forEach((g) => {
        if (visibleGroups.length > 1) {
            columnMenuItems.push(<ListSubheader key={'header-' + g.value}>{g.label}</ListSubheader>)
        }
        if (g.value === 'quote') {
            columnMenuItems.push(
                <MenuItem key={ALL_QUOTE_FIELDS} value={ALL_QUOTE_FIELDS}>
                    <Checkbox
                        checked={allQuoteSelected}
                        indeterminate={selectedQuoteCount > 0 && !allQuoteSelected}
                        color='primary'
                    />
                    <ListItemText primary='All quote fields' />
                </MenuItem>
            )
        }
        g.options.forEach((o) => {
            columnMenuItems.push(
                <MenuItem key={o.value} value={o.value}>
                    <Checkbox checked={columns.indexOf(o.value) > -1} color='primary' />
                    <ListItemText primary={o.label} />
                </MenuItem>
            )
        })
    })

    return (
        <Container maxWidth={false} component={Box} marginTop='2rem' classes={{ root: classes.containerRoot }}>
            {!loading ? (
                <Grid container justifyContent='center'>
                    <Grid item xs={12} md={8} lg={6}>
                        <Card>
                            <CardContent>
                                <Typography variant='h2' style={{ marginBottom: '1.5rem' }}>Export Leads</Typography>

                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Typography variant='h5'>Company</Typography>
                                    <FormControl variant='outlined' fullWidth>
                                        <Select
                                            value={companyId}
                                            onChange={(e) => updateCompany(e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value='' disabled>Select a company</MenuItem>
                                            {companies.map((c) => (
                                                <MenuItem key={c.companyId} value={c.companyId}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </FormGroup>

                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Typography variant='h5'>Product</Typography>
                                    <FormControl variant='outlined' fullWidth>
                                        <Select value={product} onChange={(e) => updateProduct(e.target.value)}>
                                            {productOptions.map((p) => (
                                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </FormGroup>

                                {usesBrokerDiary && (
                                    <FormGroup style={{ marginBottom: '1rem' }}>
                                        <Typography variant='h5'>Broker (optional)</Typography>
                                        <FormControl variant='outlined' fullWidth>
                                            <Select value={brokerId} onChange={(e) => setBrokerId(e.target.value)}>
                                                <MenuItem value='all'>All Brokers</MenuItem>
                                                {brokers.map((b) => (
                                                    <MenuItem key={b.brokerId} value={b.brokerId}>
                                                        {b.firstName + ' ' + b.lastName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                )}

                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Typography variant='h5'>Period</Typography>
                                    <PeriodSelect
                                        period={period}
                                        updatePeriod={(p, fd, td) => handlePeriod(p, fd, td)}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                        showAllTime
                                    />
                                </FormGroup>

                                <FormGroup style={{ marginBottom: '1.5rem' }}>
                                    <Typography variant='h5'>Columns</Typography>
                                    <FormControl variant='outlined' fullWidth>
                                        <Select
                                            multiple
                                            value={columns.filter((c) => isColumnAvailable(c, usesBrokerDiary, product))}
                                            onChange={(e) => updateColumns(e.target.value)}
                                            input={<OutlinedInput />}
                                            renderValue={(selected) =>
                                                selected
                                                    .map((v) => (getOption(v) || {}).label)
                                                    .filter(Boolean)
                                                    .join(', ')
                                            }
                                        >
                                            {columnMenuItems}
                                        </Select>
                                    </FormControl>
                                    {!usesBrokerDiary && companyId && (
                                        <Box fontSize='12px' color={theme.palette.gray[600]} marginTop='0.5rem' textAlign='left'>
                                            <Box component='small' fontSize='100%'>
                                                This company does not use Broker Diary — pipeline stage and broker are unavailable, and the allocated date is when the lead was sent to them.
                                            </Box>
                                        </Box>
                                    )}
                                </FormGroup>

                                <Button
                                    onClick={() => handleExport()}
                                    color='primary'
                                    variant='contained'
                                    fullWidth
                                    disabled={exporting}
                                >
                                    {exporting ? 'Exporting…' : 'Export to CSV'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : (
                <Grid container>
                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='center' alignItems='center' style={{ height: '20vh' }}>
                            <Loader />
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Container>
    )
}

export default ExportLeads
